import React, { useState, useRef, useMemo } from 'react';
import {
  Platform,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
} from 'react-native';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';

export function getUserDisplayName(user: any): string {
  if (user.First_Name && user.Last_Name) {
    return `${user.First_Name} ${user.Last_Name}`.trim();
  }
  if (user.Name && typeof user.Name === 'string' && user.Name.trim()) {
    return user.Name.trim().replace(/\s*Contractor\s*$/i, '').trim();
  }
  if (user.UserEmail && typeof user.UserEmail === 'string') {
    const part = user.UserEmail.split('@')[0];
    return part
      .split('.')
      .map((s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
      .join(' ');
  }
  return '';
}

export interface LocationUserSearchSelectProps {
  users: any[];
  value: string;
  onChange: (displayName: string) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
}

const LocationUserSearchSelect: React.FC<LocationUserSearchSelectProps> = ({
  users,
  value,
  onChange,
  placeholder = 'Search Navigator users…',
  hasError,
  disabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<any>(null);

  const userOptions = useMemo(() =>
    users.map(u => ({ name: getUserDisplayName(u), email: u.UserEmail ?? '' }))
      .filter(u => u.name)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return userOptions;
    return userOptions.filter(
      u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [userOptions, search]);

  const handleOpen = () => {
    if (disabled) return;
    if (open) { setOpen(false); return; }
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setDropPos({ top: y + h + 2, left: x, width: w });
      setOpen(true);
    });
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setSearch('');
    setOpen(false);
  };

  const handleClose = () => {
    setSearch('');
    setOpen(false);
  };

  return (
    <View>
      <TouchableOpacity
        ref={btnRef}
        style={[
          styles.btn,
          hasError && styles.btnError,
          disabled && styles.btnDisabled,
          isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border },
          hasError && isDark && { borderColor: AdminColors.textError },
        ]}
        onPress={handleOpen}
        activeOpacity={0.8}
        disabled={disabled}
      >
        <Text style={[styles.val, !value && styles.placeholder, isDark && { color: value ? colors.text.primary : colors.text.disabled }]} numberOfLines={1}>
          {value || placeholder}
        </Text>
        <Text style={[styles.arrow, isDark && { color: colors.text.secondary }]}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={handleClose}>
        <TouchableOpacity style={styles.backdrop} onPress={handleClose} activeOpacity={1} />
        <View
          style={[
            styles.dropdown,
            { top: dropPos.top, left: dropPos.left, minWidth: dropPos.width },
            isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
          ]}
        >
          {/* Search input */}
          <View style={[styles.searchWrap, isDark && { borderBottomColor: colors.border.divider }]}>
            <TextInput
              style={[styles.searchInput, isDark && { color: colors.text.primary }]}
              placeholder="Search by name or email…"
              placeholderTextColor={isDark ? colors.text.disabled : AdminColors.gray350}
              value={search}
              onChangeText={setSearch}
              autoFocus
            />
          </View>

          {/* User list */}
          {filtered.length === 0 ? (
            <Text style={[styles.emptyTxt, isDark && { color: colors.text.secondary }]}>No users found</Text>
          ) : (
            <FlatList
              data={filtered}
              keyExtractor={(item, i) => item.email || String(i)}
              style={styles.list}
              keyboardShouldPersistTaps="handled"
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.optRow,
                    value === item.name && styles.optRowSelected,
                    isDark && value === item.name && { backgroundColor: 'rgba(61,139,255,0.15)' },
                  ]}
                  onPress={() => handleSelect(item.name)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.optName, value === item.name && styles.optNameSelected, isDark && { color: colors.text.primary }, isDark && value === item.name && { color: colors.brand.primary }]}>
                    {item.name}
                  </Text>
                  {item.email ? (
                    <Text style={[styles.optEmail, isDark && { color: colors.text.secondary }]} numberOfLines={1}>{item.email}</Text>
                  ) : null}
                </TouchableOpacity>
              )}
            />
          )}
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    backgroundColor: AdminColors.bgSurface,
  },
  btnError: {
    borderColor: AdminColors.textError,
  },
  val: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  placeholder: {
    color: AdminColors.gray350,
  },
  arrow: {
    fontSize: 8,
    color: AdminColors.gray400,
    marginLeft: 6,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 16,
    maxHeight: 165,
  },
  searchWrap: {
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray25,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  searchInput: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    height: 30,
    padding: 0,
  },
  list: {
    maxHeight: 120,
  },
  emptyTxt: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
    padding: 14,
    textAlign: 'center',
  },
  optRow: {
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  optRowSelected: {
    backgroundColor: AdminColors.blue25,
  },
  optName: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: AdminColors.textPrimary,
  },
  optNameSelected: {
    color: AdminColors.blue700,
  },
  optEmail: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: AdminColors.textSecondary,
    marginTop: 1,
  },
});

export default LocationUserSearchSelect;
